import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle, Copy, Globe, Loader2 } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import CountrySearchSelect from "@/components/CountrySearchSelect";
import { getClientAuthToken } from "@/lib/apiAuth";
import { isGhlIntegrationActive } from "@/lib/ghlIntegrationActive";
import { WEBSITE_LEAD_INTERCEPTOR_COPY } from "@/constants/websiteLeadInterceptor";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "textarea", label: "Textarea" },
  { value: "select", label: "Select" },
  { value: "checkbox", label: "Checkbox" },
];

const MAX_CUSTOM_FIELDS = 10;

const createEmptyCustomField = () => ({
  id: "",
  type: "text",
  label: "",
  placeholder: "",
  required: false,
  options: [],
  max_length: 200,
});

const slugFromLabel = (label) =>
  label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "field_$1")
    .slice(0, 32);

const DEFAULT_FORM = {
  active: true,
  allowed_origins: "",
  default_country: "US",
  geo_ip_enabled: true,
  name_field_enabled: true,
  custom_fields: [],
  auto_open_enabled: true,
  exit_intent_enabled: true,
  engagement_open_threshold: 55,
  engagement_min_time_sec: 12,
  engagement_decay_lambda: 0.03,
  showAdvancedEngagement: false,
  google_ads_conversion_id: "",
  lead_delivery_mode: "widget",
  ghl_delivery_channel: "sms",
};

const WebWidgetIntegration = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [siteKey, setSiteKey] = useState(null);
  const [embedSnippet, setEmbedSnippet] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState("");
  const [copied, setCopied] = useState(false);
  const [ghlStatus, setGhlStatus] = useState({
    loaded: false,
    active: false,
    hasPipeline: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (ghlStatus.loaded && !ghlStatus.active) {
      setForm((prev) =>
        prev.lead_delivery_mode === "widget" ? prev : { ...prev, lead_delivery_mode: "widget" },
      );
    }
  }, [ghlStatus.loaded, ghlStatus.active]);

  const loadGhlStatus = async (token) => {
    const inactive = { loaded: true, active: false, hasPipeline: false };
    try {
      const response = await axios.get(`${BACKEND_URL}/api/integrations/ghl`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const integration = response.data?.integration || {};
      const credentials = integration.credentials || {};
      const pipeline = credentials.pipeline || {};
      const status = {
        loaded: true,
        active: isGhlIntegrationActive(response.data),
        hasPipeline: Boolean(pipeline.pipeline_id && pipeline.stage_id),
      };
      setGhlStatus(status);
      return status;
    } catch {
      setGhlStatus(inactive);
      return inactive;
    }
  };

  const hasRequiredEmailField = form.custom_fields.some(
    (field) => field.type === "email" && field.required,
  );

  const loadSettings = async () => {
    setIsLoading(true);
    setApiError("");
    try {
      const token = getClientAuthToken();
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/widget/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      const ghl = await loadGhlStatus(token);
      setSiteKey(data.site_key);
      setEmbedSnippet(data.embed_snippet || "");
      setForm({
        active: data.active ?? true,
        allowed_origins: (data.allowed_origins || []).join("\n"),
        default_country: data.default_country ?? "US",
        geo_ip_enabled: data.geo_ip_enabled ?? true,
        name_field_enabled: data.name_field_enabled ?? true,
        custom_fields: (data.custom_fields || []).map((field) => ({
          id: field.id || "",
          type: field.type || "text",
          label: field.label || "",
          placeholder: field.placeholder || "",
          required: Boolean(field.required),
          max_length: field.max_length ?? 200,
          options: (field.options || []).map((option) => ({
            value: option.value || "",
            label: option.label || "",
          })),
        })),
        auto_open_enabled: data.auto_open_enabled ?? true,
        exit_intent_enabled: data.exit_intent_enabled ?? true,
        engagement_open_threshold: data.engagement_open_threshold ?? 55,
        engagement_min_time_sec: data.engagement_min_time_sec ?? 12,
        engagement_decay_lambda: data.engagement_decay_lambda ?? 0.03,
        showAdvancedEngagement: false,
        google_ads_conversion_id: data.google_ads_conversion_id ?? "",
        lead_delivery_mode:
          ghl.active && data.lead_delivery_mode === "ghl" ? "ghl" : "widget",
        ghl_delivery_channel: data.ghl_delivery_channel ?? "sms",
      });
    } catch (err) {
      console.error("Load widget settings error:", err);
      setApiError(WEBSITE_LEAD_INTERCEPTOR_COPY.loadSettingsError);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomField = (index, patch) => {
    setForm((prev) => {
      const nextFields = [...prev.custom_fields];
      const current = { ...nextFields[index], ...patch };
      if ("label" in patch && !current.id.trim()) {
        current.id = slugFromLabel(current.label);
      }
      nextFields[index] = current;
      return { ...prev, custom_fields: nextFields };
    });
  };

  const addCustomField = () => {
    setForm((prev) => {
      if (prev.custom_fields.length >= MAX_CUSTOM_FIELDS) return prev;
      return { ...prev, custom_fields: [...prev.custom_fields, createEmptyCustomField()] };
    });
  };

  const removeCustomField = (index) => {
    setForm((prev) => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, idx) => idx !== index),
    }));
  };

  const moveCustomField = (index, direction) => {
    setForm((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.custom_fields.length) return prev;
      const nextFields = [...prev.custom_fields];
      const [item] = nextFields.splice(index, 1);
      nextFields.splice(nextIndex, 0, item);
      return { ...prev, custom_fields: nextFields };
    });
  };

  const addSelectOption = (fieldIndex) => {
    setForm((prev) => {
      const nextFields = [...prev.custom_fields];
      const field = { ...nextFields[fieldIndex] };
      field.options = [...field.options, { value: "", label: "" }];
      nextFields[fieldIndex] = field;
      return { ...prev, custom_fields: nextFields };
    });
  };

  const updateSelectOption = (fieldIndex, optionIndex, patch) => {
    setForm((prev) => {
      const nextFields = [...prev.custom_fields];
      const field = { ...nextFields[fieldIndex] };
      const options = [...field.options];
      options[optionIndex] = { ...options[optionIndex], ...patch };
      field.options = options;
      nextFields[fieldIndex] = field;
      return { ...prev, custom_fields: nextFields };
    });
  };

  const removeSelectOption = (fieldIndex, optionIndex) => {
    setForm((prev) => {
      const nextFields = [...prev.custom_fields];
      const field = { ...nextFields[fieldIndex] };
      field.options = field.options.filter((_, idx) => idx !== optionIndex);
      nextFields[fieldIndex] = field;
      return { ...prev, custom_fields: nextFields };
    });
  };

  const validateCustomFields = () => {
    const ids = new Set();
    for (const field of form.custom_fields) {
      const id = field.id.trim();
      const label = field.label.trim();
      if (!id || !/^[a-z][a-z0-9_]{0,31}$/.test(id)) {
        return "Each custom field needs a valid ID (lowercase letters, numbers, underscores).";
      }
      if (!label) {
        return "Each custom field needs a label.";
      }
      if (ids.has(id)) {
        return `Duplicate custom field ID: ${id}`;
      }
      ids.add(id);
      if (field.type === "select") {
        if (!field.options.length) {
          return `Select field "${label}" needs at least one option.`;
        }
        for (const option of field.options) {
          if (!option.value.trim() || !option.label.trim()) {
            return `Select field "${label}" has incomplete options.`;
          }
        }
      }
    }
    return null;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setApiError("");
    setSuccess(false);

    const customFieldError = validateCustomFields();
    if (customFieldError) {
      setApiError(customFieldError);
      setIsSaving(false);
      return;
    }

    const conversionId = form.google_ads_conversion_id.trim();
    if (conversionId && !/^AW-\d+(?:\/[A-Za-z0-9_-]+)?$/.test(conversionId)) {
      setApiError(WEBSITE_LEAD_INTERCEPTOR_COPY.googleAdsConversionIdInvalid);
      setIsSaving(false);
      return;
    }

    try {
      const token = getClientAuthToken();
      if (!token) {
        navigate("/login");
        return;
      }

      const allowedOrigins = form.allowed_origins
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      const response = await axios.put(
        `${BACKEND_URL}/api/widget/settings`,
        {
          active: form.active,
          allowed_origins: allowedOrigins,
          default_country: form.default_country,
          geo_ip_enabled: form.geo_ip_enabled,
          name_field_enabled: form.name_field_enabled,
          custom_fields: form.custom_fields.map((field) => ({
            id: field.id.trim(),
            type: field.type,
            label: field.label.trim(),
            placeholder: field.placeholder.trim(),
            required: Boolean(field.required),
            max_length: Number(field.max_length) || 200,
            options:
              field.type === "select"
                ? field.options.map((option) => ({
                    value: option.value.trim(),
                    label: option.label.trim(),
                  }))
                : [],
          })),
          auto_open_enabled: form.auto_open_enabled,
          exit_intent_enabled: form.exit_intent_enabled,
          engagement_open_threshold: Number(form.engagement_open_threshold),
          engagement_min_time_sec: Number(form.engagement_min_time_sec),
          engagement_decay_lambda: Number(form.engagement_decay_lambda),
          google_ads_conversion_id: form.google_ads_conversion_id.trim() || null,
          lead_delivery_mode: ghlStatus.active ? form.lead_delivery_mode : "widget",
          ghl_delivery_channel: form.ghl_delivery_channel,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSiteKey(response.data.site_key);
      setEmbedSnippet(response.data.embed_snippet || "");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Save widget settings error:", err);
      setApiError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          WEBSITE_LEAD_INTERCEPTOR_COPY.saveSettingsError,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopySnippet = async () => {
    if (!embedSnippet) return;
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setApiError("Could not copy embed snippet.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115]">
        <Sidebar />
        <div className="md:ml-64 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 text-[#00D1FF] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115]">
      <Sidebar />
      <div className="md:ml-64 min-h-screen p-6 md:p-8">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F3F4F6] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#00D1FF]/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#00D1FF]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl text-[#F3F4F6]">
                {WEBSITE_LEAD_INTERCEPTOR_COPY.productName}
              </h1>
              <p className="text-sm text-[#9CA3AF]">
                {WEBSITE_LEAD_INTERCEPTOR_COPY.settingsSubtitle}
              </p>
            </div>
          </div>

          {apiError && (
            <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {apiError}
            </div>
          )}

          {success && (
            <div className="mt-4 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {WEBSITE_LEAD_INTERCEPTOR_COPY.settingsSaved}
            </div>
          )}

          <form onSubmit={handleSave} className="mt-8 space-y-6">
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Allowed domains (one per line)</label>
              <textarea
                value={form.allowed_origins}
                onChange={(e) => setForm((prev) => ({ ...prev, allowed_origins: e.target.value }))}
                rows={4}
                placeholder={"https://yourdomain.com\nhttps://www.yourdomain.com"}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50"
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <h2 className="text-sm font-semibold text-[#F3F4F6]">Intro form</h2>
              <label className="flex items-center gap-3 text-sm text-[#F3F4F6]">
                <input
                  type="checkbox"
                  checked={form.name_field_enabled}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name_field_enabled: e.target.checked }))
                  }
                  className="rounded border-white/20"
                />
                Ask for visitor name
              </label>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-[#F3F4F6]">Custom fields</h2>
                  <p className="text-xs text-[#9CA3AF] mt-1">
                    Shown on the intro form before phone. Values are sent in the first message.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addCustomField}
                  disabled={form.custom_fields.length >= MAX_CUSTOM_FIELDS}
                  className="text-xs text-[#00D1FF] hover:text-[#7ee8ff] disabled:opacity-50"
                >
                  Add field
                </button>
              </div>

              {form.custom_fields.length === 0 ? (
                <p className="text-xs text-[#9CA3AF]">No custom fields yet.</p>
              ) : (
                form.custom_fields.map((field, index) => (
                  <div
                    key={`custom-field-${index}`}
                    className="rounded-lg border border-white/10 bg-[#080A0F]/40 p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-[#F3F4F6]">Field {index + 1}</p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => moveCustomField(index, -1)}
                          disabled={index === 0}
                          className="text-xs text-[#9CA3AF] hover:text-[#F3F4F6] disabled:opacity-40"
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          onClick={() => moveCustomField(index, 1)}
                          disabled={index === form.custom_fields.length - 1}
                          className="text-xs text-[#9CA3AF] hover:text-[#F3F4F6] disabled:opacity-40"
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          onClick={() => removeCustomField(index)}
                          className="text-xs text-red-300 hover:text-red-200"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[#9CA3AF] mb-1">Label</label>
                        <input
                          value={field.label}
                          onChange={(e) => updateCustomField(index, { label: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#9CA3AF] mb-1">ID</label>
                        <input
                          value={field.id}
                          onChange={(e) => updateCustomField(index, { id: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6] font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#9CA3AF] mb-1">Type</label>
                        <select
                          value={field.type}
                          onChange={(e) =>
                            updateCustomField(index, {
                              type: e.target.value,
                              options: e.target.value === "select" ? field.options : [],
                            })
                          }
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6]"
                        >
                          {FIELD_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {field.type !== "checkbox" && field.type !== "select" ? (
                        <div>
                          <label className="block text-xs text-[#9CA3AF] mb-1">Placeholder</label>
                          <input
                            value={field.placeholder}
                            onChange={(e) => updateCustomField(index, { placeholder: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6]"
                          />
                        </div>
                      ) : null}
                    </div>

                    <label className="flex items-center gap-3 text-sm text-[#F3F4F6]">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateCustomField(index, { required: e.target.checked })}
                        className="rounded border-white/20"
                      />
                      Required
                    </label>

                    {field.type === "select" ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-[#9CA3AF]">Options</p>
                          <button
                            type="button"
                            onClick={() => addSelectOption(index)}
                            className="text-xs text-[#00D1FF] hover:text-[#7ee8ff]"
                          >
                            Add option
                          </button>
                        </div>
                        {field.options.map((option, optionIndex) => (
                          <div key={`option-${index}-${optionIndex}`} className="grid md:grid-cols-[1fr_1fr_auto] gap-2">
                            <input
                              value={option.value}
                              placeholder="Value"
                              onChange={(e) =>
                                updateSelectOption(index, optionIndex, { value: e.target.value })
                              }
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6]"
                            />
                            <input
                              value={option.label}
                              placeholder="Label"
                              onChange={(e) =>
                                updateSelectOption(index, optionIndex, { label: e.target.value })
                              }
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6]"
                            />
                            <button
                              type="button"
                              onClick={() => removeSelectOption(index, optionIndex)}
                              className="text-xs text-red-300 hover:text-red-200 px-2"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <h2 className="text-sm font-semibold text-[#F3F4F6]">Phone settings</h2>
              <p className="text-xs text-[#9CA3AF]">
                Used when the visitor&apos;s country cannot be detected automatically.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="default-country" className="block text-sm text-[#9CA3AF] mb-2">
                    Default country
                  </label>
                  <CountrySearchSelect
                    id="default-country"
                    value={form.default_country}
                    onChange={(code) => setForm((prev) => ({ ...prev, default_country: code }))}
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 text-sm text-[#F3F4F6] pb-3">
                    <input
                      type="checkbox"
                      checked={form.geo_ip_enabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, geo_ip_enabled: e.target.checked }))}
                      className="rounded border-white/20"
                    />
                    Auto-detect country by IP
                  </label>
                </div>
              </div>
            </div>

            {ghlStatus.loaded && ghlStatus.active && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <h2 className="text-sm font-semibold text-[#F3F4F6]">
                {WEBSITE_LEAD_INTERCEPTOR_COPY.deliverySectionTitle}
              </h2>
              <p className="text-xs text-[#9CA3AF]">
                {WEBSITE_LEAD_INTERCEPTOR_COPY.deliverySectionHelp}
              </p>

              <div className="space-y-3">
                <label className="flex items-start gap-3 text-sm text-[#F3F4F6] cursor-pointer">
                  <input
                    type="radio"
                    name="lead_delivery_mode"
                    value="widget"
                    checked={form.lead_delivery_mode === "widget"}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, lead_delivery_mode: "widget" }))
                    }
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">{WEBSITE_LEAD_INTERCEPTOR_COPY.deliveryModeWidget}</span>
                    <span className="block text-xs text-[#9CA3AF] mt-0.5">
                      {WEBSITE_LEAD_INTERCEPTOR_COPY.deliveryModeWidgetHelp}
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3 text-sm text-[#F3F4F6] cursor-pointer">
                  <input
                    type="radio"
                    name="lead_delivery_mode"
                    value="ghl"
                    checked={form.lead_delivery_mode === "ghl"}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, lead_delivery_mode: "ghl" }))
                    }
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">{WEBSITE_LEAD_INTERCEPTOR_COPY.deliveryModeGhl}</span>
                    <span className="block text-xs text-[#9CA3AF] mt-0.5">
                      {WEBSITE_LEAD_INTERCEPTOR_COPY.deliveryModeGhlHelp}
                    </span>
                  </span>
                </label>
              </div>

              {form.lead_delivery_mode === "ghl" && (
                <div className="space-y-4 pt-2 border-t border-white/10">
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-[#F3F4F6] cursor-pointer">
                      <input
                        type="radio"
                        name="ghl_delivery_channel"
                        value="sms"
                        checked={form.ghl_delivery_channel === "sms"}
                        onChange={() =>
                          setForm((prev) => ({ ...prev, ghl_delivery_channel: "sms" }))
                        }
                      />
                      {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlChannelSms}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[#F3F4F6] cursor-pointer">
                      <input
                        type="radio"
                        name="ghl_delivery_channel"
                        value="email"
                        checked={form.ghl_delivery_channel === "email"}
                        onChange={() =>
                          setForm((prev) => ({ ...prev, ghl_delivery_channel: "email" }))
                        }
                      />
                      {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlChannelEmail}
                    </label>
                  </div>

                  <div className="rounded-lg bg-white/5 border border-white/10 p-3 space-y-2">
                    <p className="text-xs font-medium text-[#F3F4F6]">
                      {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlPrerequisitesTitle}
                    </p>
                    <ul className="text-xs space-y-1">
                      <li className={ghlStatus.active ? "text-emerald-400" : "text-[#9CA3AF]"}>
                        {ghlStatus.active ? "✓" : "○"} {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlPrerequisiteIntegration}
                      </li>
                      <li className={ghlStatus.hasPipeline ? "text-emerald-400" : "text-[#9CA3AF]"}>
                        {ghlStatus.hasPipeline ? "✓" : "○"} {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlPrerequisitePipeline}
                      </li>
                      {form.ghl_delivery_channel === "email" && (
                        <li className={hasRequiredEmailField ? "text-emerald-400" : "text-amber-400"}>
                          {hasRequiredEmailField ? "✓" : "○"}{" "}
                          {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlPrerequisiteEmailField}
                        </li>
                      )}
                    </ul>
                    <button
                      type="button"
                      onClick={() => navigate("/dashboard/integrations/ghl")}
                      className="text-xs text-[#00D1FF] hover:text-[#7ee8ff] mt-1"
                    >
                      {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlConfigureLink} →
                    </button>
                  </div>

                  <p className="text-xs text-[#9CA3AF]/80">
                    {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlDeliveryViaGhlHint}
                  </p>
                </div>
              )}
            </div>
            )}

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <h2 className="text-sm font-semibold text-[#F3F4F6]">
                {WEBSITE_LEAD_INTERCEPTOR_COPY.googleAdsSectionTitle}
              </h2>
              <p className="text-xs text-[#9CA3AF]">
                {WEBSITE_LEAD_INTERCEPTOR_COPY.googleAdsConversionIdHelp}
              </p>
              <div>
                <label htmlFor="google-ads-conversion-id" className="block text-sm text-[#9CA3AF] mb-2">
                  {WEBSITE_LEAD_INTERCEPTOR_COPY.googleAdsConversionIdLabel}
                </label>
                <input
                  id="google-ads-conversion-id"
                  type="text"
                  value={form.google_ads_conversion_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, google_ads_conversion_id: e.target.value }))
                  }
                  placeholder="AW-123456789/AbCdEfGhIj"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F3F4F6] font-mono"
                />
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <h2 className="text-sm font-semibold text-[#F3F4F6]">Auto-open (engagement)</h2>
              <p className="text-xs text-[#9CA3AF]">
                Opens once when engagement score is high enough — not on fast scroll.
              </p>
              <label className="flex items-center gap-3 text-sm text-[#F3F4F6]">
                <input
                  type="checkbox"
                  checked={form.auto_open_enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, auto_open_enabled: e.target.checked }))}
                  className="rounded border-white/20"
                />
                Enable smart auto-open
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="engagement-threshold" className="block text-sm text-[#9CA3AF] mb-2">
                    Open threshold (30–90)
                  </label>
                  <input
                    id="engagement-threshold"
                    type="number"
                    min={30}
                    max={90}
                    value={form.engagement_open_threshold}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, engagement_open_threshold: e.target.value }))
                    }
                    disabled={!form.auto_open_enabled}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="engagement-min-time" className="block text-sm text-[#9CA3AF] mb-2">
                    Min time on page (sec)
                  </label>
                  <input
                    id="engagement-min-time"
                    type="number"
                    min={5}
                    max={120}
                    value={form.engagement_min_time_sec}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, engagement_min_time_sec: e.target.value }))
                    }
                    disabled={!form.auto_open_enabled}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-50"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    showAdvancedEngagement: !prev.showAdvancedEngagement,
                  }))
                }
                className="text-xs text-[#00D1FF] hover:text-[#7ee8ff]"
              >
                {form.showAdvancedEngagement ? "Hide advanced" : "Show advanced"}
              </button>
              {form.showAdvancedEngagement && (
                <div className="pt-2">
                  <label htmlFor="engagement-decay" className="block text-sm text-[#9CA3AF] mb-2">
                    Decay lambda (0.01–0.1)
                  </label>
                  <input
                    id="engagement-decay"
                    type="number"
                    min={0.01}
                    max={0.1}
                    step={0.01}
                    value={form.engagement_decay_lambda}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, engagement_decay_lambda: e.target.value }))
                    }
                    disabled={!form.auto_open_enabled}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-50"
                  />
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <h2 className="text-sm font-semibold text-[#F3F4F6]">Exit intent</h2>
              <p className="text-xs text-[#9CA3AF]">
                Opens immediately when the visitor moves to close the tab — regardless of engagement score. Once per visit. Desktop only.
              </p>
              <label className="flex items-center gap-3 text-sm text-[#F3F4F6]">
                <input
                  type="checkbox"
                  checked={form.exit_intent_enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, exit_intent_enabled: e.target.checked }))}
                  className="rounded border-white/20"
                />
                Open on exit intent (desktop)
              </label>
            </div>

            <label className="flex items-center gap-3 text-sm text-[#F3F4F6]">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                className="rounded border-white/20"
              />
              {WEBSITE_LEAD_INTERCEPTOR_COPY.activeLabel}
            </label>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 bg-[#00D1FF] text-[#0F1115] font-semibold rounded-lg px-6 py-3 hover:bg-[#00D1FF]/90 disabled:opacity-60"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save settings
            </button>
          </form>

          {siteKey && (
            <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Site key</p>
                <p className="font-mono text-sm text-[#F3F4F6] break-all">{siteKey}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider text-[#9CA3AF]">Embed snippet</p>
                  <button
                    type="button"
                    onClick={handleCopySnippet}
                    className="inline-flex items-center gap-1 text-xs text-[#00D1FF] hover:text-[#7ee8ff]"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre className="overflow-x-auto rounded-lg bg-[#080A0F] border border-white/10 p-4 text-xs text-[#D1D5DB] whitespace-pre-wrap">
                  {embedSnippet}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebWidgetIntegration;
